import { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { toast } from "react-toastify";
import {
  useGetFleetRulesQuery,
  useListComplianceTrainingsQuery,
  useUpdateFleetRulesMutation,
  useUpdateRequiredRatingsMutation,
} from "../../redux/api/complianceTraining";
import { useGetFleetsQuery } from "../../redux/api/fleet";
import { ComplianceTraining, ComplianceTrainingFleetRule } from "../../common/ComplianceTraining";
import { JobRating, JobRatingLabel } from "../../common/JobOrder";

const ALL_RATINGS = Object.values(JobRating).map((r) => r as string);

const ratingOption = (r: string) => ({
  value: r,
  label: (JobRatingLabel as Record<string, string>)[r] ?? r,
});

const ComplianceTrainingSettingsModal = () => {
  const [open, setOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState<ComplianceTraining | null>(null);
  const [pendingRatings, setPendingRatings] = useState<string[]>([]);
  const [pendingFleetRules, setPendingFleetRules] = useState<ComplianceTrainingFleetRule[]>([]);
  const [saving, setSaving] = useState(false);

  const { data: trainings, refetch: refetchTrainings } = useListComplianceTrainingsQuery();
  const { data: fleets } = useGetFleetsQuery({});
  const { data: existingRules, refetch: refetchFleetRules } = useGetFleetRulesQuery(
    selectedTraining?.id ?? skipToken
  );

  const [updateRequiredRatings] = useUpdateRequiredRatingsMutation();
  const [updateFleetRules] = useUpdateFleetRulesMutation();

  useEffect(() => {
    if (!selectedTraining) return;
    const currentTraining = trainings?.find((t) => t.id === selectedTraining.id) ?? selectedTraining;
    setPendingRatings(currentTraining.requiredRatings ?? []);
    if (fleets && existingRules !== undefined) {
      setPendingFleetRules(
        fleets.map((f) => {
          const existing = existingRules.find((r) => r.fleetId === f.id);
          return existing ?? { fleetId: f.id!, disabled: false, requiredRatings: [] };
        })
      );
    }
  }, [selectedTraining, trainings, fleets, existingRules]);

  const handleClose = () => {
    setOpen(false);
    setSelectedTraining(null);
    setPendingRatings([]);
    setPendingFleetRules([]);
  };

  const updateFleetRule = (fleetId: number, patch: Partial<ComplianceTrainingFleetRule>) => {
    setPendingFleetRules((prev) =>
      prev.map((r) => (r.fleetId === fleetId ? { ...r, ...patch } : r))
    );
  };

  const handleSave = async () => {
    if (!selectedTraining) return;
    setSaving(true);
    try {
      await updateRequiredRatings({ trainingId: selectedTraining.id, ratings: pendingRatings }).unwrap();
      await updateFleetRules({ trainingId: selectedTraining.id, rules: pendingFleetRules }).unwrap();
      refetchTrainings();
      refetchFleetRules();
      toast.success("Training requirements updated");
    } catch {
      toast.error("Failed to save training requirements");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button className="grey-link-button" onClick={() => setOpen(true)} title="Training Settings">
        <SettingsIcon />
      </Button>
      <Dialog
        className="modal compliance-upload-dialog"
        open={open}
        onClose={handleClose}
      >
        <div className="modal-header mb-2">
          <Typography variant="h3">Compliance Training Requirements</Typography>
          <Button onClick={handleClose} className="grey-link-button">
            <CloseIcon />
          </Button>
        </div>
        <div className="form-narrow">
          <div>
            <InputLabel>Training</InputLabel>
            <Autocomplete
              options={trainings ?? []}
              value={selectedTraining}
              onChange={(_e, val) => setSelectedTraining(val)}
              getOptionLabel={(t) => t.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} placeholder="Select Training" />}
            />
          </div>

          {selectedTraining && (
            <>
              <div>
                <InputLabel>Global Required Ratings</InputLabel>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Leave empty to require for all ratings.
                </Typography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={ALL_RATINGS.map(ratingOption)}
                  value={pendingRatings.map(ratingOption)}
                  onChange={(_e, vals) => setPendingRatings(vals.map((v) => v.value))}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  renderInput={(params) => <TextField {...params} placeholder="All Ratings" />}
                />
              </div>

              <div>
                <InputLabel>Fleet-Specific Overrides</InputLabel>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Empty ratings falls back to the global settings.
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fleet</TableCell>
                      <TableCell>Disabled</TableCell>
                      <TableCell>Rating override</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingFleetRules.map((rule) => {
                      const fleet = fleets?.find((f) => f.id === rule.fleetId);
                      return (
                        <TableRow key={rule.fleetId}>
                          <TableCell>{fleet?.name ?? rule.fleetId}</TableCell>
                          <TableCell>
                            <Checkbox
                              checked={rule.disabled}
                              onChange={(e) =>
                                updateFleetRule(rule.fleetId, {
                                  disabled: e.target.checked,
                                  requiredRatings: e.target.checked ? [] : rule.requiredRatings,
                                })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Autocomplete
                              multiple
                              disableCloseOnSelect
                              disabled={rule.disabled}
                              options={ALL_RATINGS.map(ratingOption)}
                              value={rule.requiredRatings.map(ratingOption)}
                              onChange={(_e, vals) =>
                                updateFleetRule(rule.fleetId, { requiredRatings: vals.map((v) => v.value) })
                              }
                              getOptionLabel={(o) => o.label}
                              isOptionEqualToValue={(a, b) => a.value === b.value}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Use Global" />
                              )}
                              sx={{ minWidth: 260 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
        <div className="modal-button">
          <Button className="outline-button-small" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="blue-button-small"
            onClick={handleSave}
            disabled={!selectedTraining || saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default ComplianceTrainingSettingsModal;
